import { Ref, Vue } from "vue-property-decorator";
import { Options, setup } from "vue-class-component";
import Dialog from "primevue/dialog";
import {
    PopupDialogButton,
    popupDialogModel,
} from "@/core/popup-dialog/popup-dialog.model";
import { useRouter } from "vue-router";
import * as yup from "yup";
import { useField, useForm } from "vee-validate";
import { shallowRef, watch } from "vue";
import { useLogin } from "@/core/auth/useCases/useLogin";
import BaseInput from "@/shared/components/base/BaseInput.vue";
import { useI18n } from "@/shared/composables/use-i18n";
import { appModel } from "@/app.model";
import { routerModel } from "@/router/router.model";
import { sessionModel } from "@/shared/state/session.model";
import { getAuthRedirectService } from "../services/auth-redirects.service";

@Options({
    components: {
        Dialog,
    },
})
export class PopupDialog extends Vue {
    @Ref("passwordInput") passwordInput!: BaseInput;
    i18n = setup(useI18n);

    login = setup(() => {
        const schema = yup.object({
            username: yup.string().required().email(),
            password: yup.string().required().min(4),
        });

        const { handleSubmit } = useForm({
            validationSchema: schema,
        });
        const isAuthenticated = shallowRef(false);
        const isInvalidCredentials = shallowRef(false);
        const { value: username, errorMessage: usernameError } = useField("username");
        const { value: password, errorMessage: passwordError } = useField("password");

        const onSubmit = handleSubmit(async (values) => {
            try {
                const apiResponse = await useLogin(
                    values.username as string,
                    values.password as string,
                );

                if (apiResponse) sessionModel.setSessionDto(apiResponse.data);
                isAuthenticated.value = !!apiResponse;
                if (isAuthenticated.value) {
                    this.model.show = false;
                    this.model.isLoginPopupOpen = false;
                    getAuthRedirectService().doRedirectAfterLogin();
                }
                isInvalidCredentials.value = !isAuthenticated.value;
            } catch (e) {
                console.log(e);
            }
        });
        return {
            username,
            usernameError,
            onSubmit,
            password,
            passwordError,
            isAuthenticated,
            isInvalidCredentials,
        };
    });

    model = setup(() => popupDialogModel);
    router = setup(useRouter);

    mounted() {
        watch(
            () => ({
                isAuthenticated: this.login.isAuthenticated,
                isInvalidCredentials: this.login.isInvalidCredentials,
                usernameError: this.login.usernameError,
            }),
            ({ isAuthenticated, isInvalidCredentials }) => {
                if (isInvalidCredentials) {
                    this.login.isInvalidCredentials = false;
                    appModel.toast.value.add({
                        severity: "error",
                        summary: this.i18n.t("auth.incorrectAuth"),
                        life: 3000,
                    });
                }

                if (isAuthenticated) {
                    this.model.show = false;
                    this.model.isLoginPopupOpen = false;
                }
            },
        );
    }

    getButtonClass(button: PopupDialogButton) {
        return [button.design ? "button--" + button.design : ""];
    }

    async doButtonAction(button: PopupDialogButton) {
        if (button.link) {
            this.router.push(button.link);
            this.model.show = false;
        } else if (button.callback) {
            const callbackResult = button.callback();
            if (callbackResult instanceof Promise) {
                callbackResult.then(() => {
                    this.model.show = false;
                });
            } else {
                this.model.show = false;
            }
        } else {
            throw Error("link or callback should be specified for button");
        }
    }

    togglePassword() {
        this.passwordInput.$el.type =
            this.passwordInput.$el.type === "password" ? "text" : "password";
    }

    get signUpURL(): string | undefined {
        if (routerModel.router.currentRoute.value.name !== "app-role-validation") {
            return undefined;
        }

        return `/sign-up/${routerModel.router.currentRoute.value.params.clientId}/${routerModel.router.currentRoute.value.params.companyEmail}`;
    }
}
